import { useEffect, useRef } from 'react'
import { createLogger } from '../utils/logger'

const logger = createLogger('ChannelAutoSelect')

/**
 * useChannelAutoSelect - Manages automatic channel selection with progressive retry
 * 
 * Implements AIM Atomicity principle: waits for target channel to be available
 * before attempting selection, with progressive retry logic (500ms intervals, max 5 attempts).
 * Prevents WebSocket connection to wrong channel during transition.
 * 
 * @param {Array} channels - Available channels
 * @param {string} initialChannelId - Target channel ID to auto-select
 * @param {Object} activeChannel - Currently active channel
 * @param {Function} setActiveChannel - Function to set active channel
 * @param {Function} setActiveTab - Function to set active tab
 * @param {Function} onChannelSelected - Optional callback when channel is selected
 * 
 * @atomic - <100 lines, single purpose
 * @idempotent - Same initialChannelId always selects same channel
 */
export function useChannelAutoSelect(
  channels,
  initialChannelId,
  activeChannel,
  setActiveChannel,
  setActiveTab,
  onChannelSelected
) {
  const hasAutoSelectedRef = useRef(false)
  const refetchAttemptsRef = useRef(0)
  const maxRefetchAttempts = 5

  useEffect(() => {
    // AIM Atomicity: Progressive retry for channel auto-selection
    // This ensures we only connect WebSocket after correct channel is selected
    if (!initialChannelId || hasAutoSelectedRef.current) {
      return
    }

    // Check if the target channel is in the list
    const targetChannel = channels.find(ch => ch.id === initialChannelId)
    
    if (targetChannel) {
      // Found the channel - select it atomically
      setActiveChannel(targetChannel)
      hasAutoSelectedRef.current = true
      refetchAttemptsRef.current = 0
      
      // Set the correct tab based on channel type
      if (targetChannel.channelType === 'entity') {
        setActiveTab('user')
      } else if (targetChannel.channelType === 'default') {
        setActiveTab('admin')
      }
      
      logger.info('Auto-selected channel', { 
        channelId: initialChannelId,
        channelName: targetChannel.name,
        channelType: targetChannel.channelType,
        attempts: refetchAttemptsRef.current + 1
      })
      
      // Notify parent component
      if (onChannelSelected) {
        onChannelSelected(targetChannel)
      }
    } else if (channels.length > 0 && refetchAttemptsRef.current < maxRefetchAttempts) {
      // Channel not found yet, but we have channels - might be a newly created channel
      // Retry with progressive delay (AIM: atomic operation, don't select wrong channel)
      refetchAttemptsRef.current += 1
      
      logger.info('Channel not found, retrying...', {
        channelId: initialChannelId,
        attempt: refetchAttemptsRef.current,
        maxAttempts: maxRefetchAttempts,
        availableChannels: channels.length
      })
      
      const retryDelay = 500 * refetchAttemptsRef.current
      const timeoutId = setTimeout(() => {
        // Force re-evaluation by checking again
        const found = channels.find(ch => ch.id === initialChannelId)
        if (!found) {
          logger.warn('Channel still not found after retry', {
            channelId: initialChannelId,
            attempt: refetchAttemptsRef.current
          })
        }
      }, retryDelay)
      
      return () => clearTimeout(timeoutId)
    } else if (refetchAttemptsRef.current >= maxRefetchAttempts) {
      // Max retries reached - log warning and give up
      logger.warn('Failed to auto-select channel after max retries', {
        channelId: initialChannelId,
        attempts: refetchAttemptsRef.current,
        availableChannels: channels.length
      })
      hasAutoSelectedRef.current = true // Prevent further attempts
      
      // Notify parent that selection failed
      if (onChannelSelected) {
        onChannelSelected(null)
      }
    }
  }, [channels, initialChannelId, activeChannel, setActiveChannel, setActiveTab, onChannelSelected])

  return {
    hasAutoSelected: hasAutoSelectedRef.current,
    refetchAttempts: refetchAttemptsRef.current
  }
}
